<template>
  <div class="purchase-order-form">
    <form @submit.stop.prevent="form.handleSubmit()">
      <!-- Project, Purchase Type, PO# -->
      <div class="grid grid-cols-9 gap-4">
        <form.Field
          name="projectId"
          :validators="{
            onBlur: yup.string().required('Project is required'),
          }"
        >
          <template #default="{ field }">
            <BaseSelect
              :model-value="(field.state.value as string)"
              :name="field.name"
              label="Project"
              :options="projects"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              class="col-span-3"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <form.Field
          name="purchaseType"
          :validators="{
            onBlur: yup.string().required('Purchase Type is required'),
          }"
        >
          <template #default="{ field }">
            <BaseSelect
              :model-value="(field.state.value as string)"
              :name="field.name"
              label="Purchase Type"
              :options="purchaseTypeOptions"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              class="col-span-4"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <form.Field
          name="purchaseOrderId"
          :validators="{
            onBlur: yup.string().required('PO# is required'),
          }"

        >
          <template #default="{ field }">
            <BaseInput
              :model-value="(field.state.value as string)"
              :name="field.name"
              type="text"
              label="PO#"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              class="col-span-2"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>
      </div>

      <div class="grid grid-cols-3 gap-4">
        <form.Field
          name="vendorId"
          :validators="{
            onBlur: yup.string().required('Vendor is required'),
          }"
        >
          <template #default="{ field }">
            <BaseSelect
              :model-value="(field.state.value as string)"
              :name="field.name"
              label="Vendor"
              :options="vendors"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <form.Field
          name="date"
          :validators="{
            onBlur: yup.string().required('Date is required'),
          }"
        >
          <template #default="{ field }">
            <BaseInputDate
              :model-value="(field.state.value as string)"
              :name="field.name"
              label="Purchase Order Date"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e.target.value)"
            />
          </template>
        </form.Field>

        <form.Field
          name="shippingAddress"
          :validators="{
            onChange: yup.string().required('Address is required')
          }"
        >
          <template #default="{ field }">
            <AddressAutoComplete
              :name="field.name"
              :value="field.state.value"
              label="Shipping Address"
              placeholder="Address"
              :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
              @blur="field.handleBlur"
              @input="(e) => field.handleChange(e)"
            />
          </template>
        </form.Field>
      </div>

      <h4 class="mt-6">Items</h4>
      <hr class="mt-2">

      <div v-if="lineItemsLoading || purchaseOrdersLoading" class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
        <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
        <p class="text-sm text-center text-slate-500 pt-4">
          Loading...
        </p>
      </div>

      <!-- items -->
      <div v-else>
        <form.Field
          name="items"
          :validators="{
            onChange: yup.array().min(1, 'At least one item is required')
          }"
        >
          <template #default="{ field }">
            <div
              v-if="(field.state.meta.errors && field.state.meta.errors[0]) && form.getFieldValue('items').length === 0"
              class="text-base/6 text-error sm:text-sm/6 mt-2 flex flex-col items-center"
            >
              <span class="i-heroicons-exclaimation-circle size-8" />
              <p class="text-base/6 text-error sm:text-sm/6">
                {{ field.state.meta.errors[0] }}
              </p>
            </div>

            <div
              v-for="(_, i) of field.state.value"
              :key="i"
            >
              <div class="flex justify-end mt-8">
                <span
                  class="i-heroicons-x-circle cursor-pointer hover:text-error h-[1.2rem] w-[1.2rem]"
                  @click="removeItem(field, Number(i))"
                />
              </div>

              <!-- Title, Quantity, Price -->
              <div class="flex justify-around gap-6">
                <div class="grow">
                  <form.Field
                    :key="`title-${i}`"
                    :name="( `items[${i}].name` as any)"
                    :validators="{
                      onBlur: yup.string().required('Title is required'),
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        :model-value="(subField.state.value as string)"
                        type="text"
                        label="Title"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => subField.handleChange(e.target.value)"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="mx-2">
                  <form.Field
                    :key="`quantity-${i}`"
                    :name="(`items[${i}].quantity` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Quantity must be a positive number',(value) => !isNaN(Number(value)) && Number(value) > 0).required('Quantity is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        v-model.number="(subField.state.value as string)"
                        type="number"
                        step="any"
                        label="Quantity"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="grow">
                  <form.Field
                    :key="`cost-${i}`"
                    :name="(`items[${i}].cost` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Price must be a valid number',(value) => !isNaN(Number(value))).required('Price is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInputCurrency
                        :model-value="(subField.state.value as string)"
                        label="Price"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <div class="mx-2 min-w-24">
                  <label
                    class="font-semibold"
                    for="total-price"
                  >Total</label>
                  <span
                    id="total-price"
                    class="block mt-3"
                  >{{ currency(toNumber((field.state.value[i] as any).quantity) * toNumber((field.state.value[i] as any).cost)) }}</span>
                </div>
              </div>
            </div>

            <div class="my-5">
              <BaseButton
                variant="secondary"
                type="button"
                text="Add item"
                :disabled="!isProjectSelected"
                :class="!isProjectSelected ? 'opacity-50' : ''"
                @click="addItem(field)"
              />
            </div>
          </template>
        </form.Field>
      </div>

      <div
        v-if="isProjectSelected"
        class="mt-5 w-fit ml-auto"
      >
        <div class="flex items-baseline justify-end bg-slate-100 px-4 pt-4 rounded-lg gap-4">
          <span class="text-slate-700">Total</span>
          <form.Field
            name="total"
            :validators="{
              onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Total must be a valid number',(value) => !isNaN(Number(value))).required('Total is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputCurrency
                :model-value="(field.state.value as string)"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="backTo ? { path: backTo } : { name: 'ListPurchaseOrders' }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>

  </div>
</template>
<script lang="ts" setup>
import { PurchaseOrder, PurchaseOrderItem, PO_PURCHASE_TYPE, generatePurchaseOrderId } from '@/models/purchaseOrder'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import { BudgetItem } from '@/models/budget'
import { map, findWhere, extend, flatten, difference, intersection } from 'underscore'
import BaseSelect from '@/components/base/BaseSelect.vue'
import AddressAutoComplete from '@/components/base/AddressAutoComplete.vue'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import { PROJECT_STATUS } from '@/models/project'
import { Material } from '@/models/material'
import { currency, toNumber } from '@/utils'
import { useForm } from '@tanstack/vue-form'
import { computed, onMounted, PropType, Ref, ref, watch } from 'vue'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import BudgetService from '@/services/BudgetService'
import { decimalFormat } from '@/utils'
import { useAuth } from '@/composables/useAuth'
import { useNotification } from '@kyvg/vue3-notification'
import { useRoute } from 'vue-router'

type selectOption = {
  label: string
  value: string,
  disabled?: boolean
  [key: string]: any
}

const props = defineProps({
  projectId: {
    type: String,
    default: ''
  },
  projects: {
    type: Array as PropType<selectOption[]>,
    default: () => []
  },
  vendors: {
    type: Array as PropType<selectOption[]>,
    default: () => []
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const purchaseTypeOptions = [
  { value: PO_PURCHASE_TYPE.INVOICE, label: 'Purchase Order (vendor will invoice)' },
  { value: PO_PURCHASE_TYPE.EXPENSE, label: 'Expense (paid with cash or debit)' },
  { value: PO_PURCHASE_TYPE.REIMBURSEMENT, label: 'Reimbursement (company will reimburse me)' }
]
const { currentUser } = useAuth()
const { notify } = useNotification()
const queryClient = useQueryClient()
const backTo = useRoute().query.backTo as string || ''
const purchaseOrder: Ref<PurchaseOrder> = ref(new PurchaseOrder)
purchaseOrder.value.projectId = props.projectId

const form = useForm({
  defaultValues: purchaseOrder.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const purchaseOrder = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as PurchaseOrder

    purchaseOrder.items = purchaseOrder.items.map((item) => {
      const poItem = {
        ...item,
        cost: toNumber(item.cost),
        quantity: toNumber(item.quantity)
      }
      return Object.fromEntries(Object.entries(poItem).filter(([, v]) => v !== undefined)) as unknown as PurchaseOrderItem
    })
    purchaseOrder.date = new Date(purchaseOrder.date)
    purchaseOrder.projectId = purchaseOrder.projectId
    purchaseOrder.submittedBy = currentUser.value.id
    purchaseOrder.total = toNumber(purchaseOrder.total)

    await props.saveFunction(purchaseOrder)
    queryClient.invalidateQueries({ queryKey: ['PurchaseOrders', selectedProjectId.value] })
  },
})

const selectedProjectId = form.useStore((state) => state.values.projectId)
const isProjectSelected = computed(() =>  selectedProjectId.value !== '')
const budgetService = new BudgetService()
const enablePOQuery = computed(() => budgetLineItems.value?.length > 0)

const { data:budgetLineItems, isLoading:lineItemsLoading } = useQuery({
  queryKey: ['LineItems', selectedProjectId],
  queryFn: () => budgetService.getLineItems(selectedProjectId.value),
  enabled: isProjectSelected
})

const { data:otherPurchaseOrders, isLoading:purchaseOrdersLoading } = useQuery({
  queryKey: ['PurchaseOrders', selectedProjectId],
  queryFn: async () => await budgetService.getPurchaseOrders(selectedProjectId.value),
  enabled: enablePOQuery
})

watch(() => [selectedProjectId.value, isProjectSelected.value, purchaseOrdersLoading.value, lineItemsLoading.value], () => {
  const allLoaded = selectedProjectId.value && isProjectSelected.value && !purchaseOrdersLoading.value && !lineItemsLoading.value
  if (allLoaded && otherPurchaseOrders.value && budgetLineItems.value) {
    projectSelected(selectedProjectId.value, budgetLineItems.value, otherPurchaseOrders.value)
  }
}, { immediate: true })

const addItem = (field: any) => {
  field.pushValue({
    ...new PurchaseOrderItem(),
    quantity: '1',
    cost: '0'
  })
}

const removeItem = (field: any, index: number) => {
  // save lineItemIds of the item to delete
  const poItem = { ...form.getFieldValue('items')[index] }
  let poItemLineItems = extend([], poItem.lineItems)

  // delete the item
  field.removeValue(index)

  // does the itemToDelete lineitemsIds match any of the other item lineitemsIds
  const match = map(form.getFieldValue('items'), (item: PurchaseOrderItem) => {
    return intersection(poItemLineItems, item.lineItems)
  })

  const noMatchFound = (!flatten(match)[0])
  if (noMatchFound) {
    // delete lineItemIds not matching any other
    form.setFieldValue('lineItems', difference(form.getFieldValue('lineItems'), poItemLineItems))
    poItemLineItems = []
  }
  setTotal()
}

const setTotal = () => {
  const total = form.getFieldValue('items').reduce((acc, item) => {
    return acc + (item.quantity * toNumber(item.cost))
  }, 0)

  if (total) {
    form.setFieldValue('total', decimalFormat(total, 2) as any)
  } else {
    form.setFieldValue('total', String(0) as any)
  }
}

const projectSelected = async  (projectId, budgetLineItems: BudgetItem[], purchaseOrders: PurchaseOrder[]) => {
  const selectedProject = props.projects.find((project) => project.value === projectId)
  const lineItems = JSON.parse(JSON.stringify(budgetLineItems)) as BudgetItem[]

  if (selectedProject.value && selectedProject.status === PROJECT_STATUS.IN_PROGRESS) {
    try {
      form.setFieldValue('shippingAddress', selectedProject.address ?? '')
      form.setFieldValue('budgetId', selectedProject.value)

      const itemsOnExistingPurchaseOrder = flatten(purchaseOrders.map((PurchaseOrder) => { return PurchaseOrder.lineItems }))
      const purchaseOrderItems = []
      form.setFieldValue('lineItems', [])

      lineItems.forEach((item: BudgetItem) => {
        if (item.selected && !item.isCreditLineItem) {
          if (itemsOnExistingPurchaseOrder.indexOf(item.id) === -1) {
            if (item.materials.length === 0) {
              const material = new Material()

              material.id = item.id
              material.quantity = 1
              material.cost = item.materialsBudget ? item.materialsBudget : 0.00
              material.name = item.title

              item.materials = [material]
            }

            item.materials.forEach((material) => {
              const purchaseOrderItem = extend({ lineItems: [item.id] }, {
                ...material,
                cost: toNumber(material.cost),
                quantity: toNumber(material.quantity)
              })

              const existingPurchaseOrderItem = findWhere(purchaseOrderItems, { id: material.id })

              if (existingPurchaseOrderItem) {
                existingPurchaseOrderItem.quantity += purchaseOrderItem.quantity
                existingPurchaseOrderItem.lineItems.push(item.id)
              } else {
                purchaseOrderItems.push(purchaseOrderItem)
              }

              const itemIdNotinArray = form.getFieldValue('lineItems').indexOf(item.id) === -1
              if (itemIdNotinArray) form.pushFieldValue('lineItems', item.id)
            })
          }
        }
      })

      form.setFieldValue('items', purchaseOrderItems)
      setTotal()
    } catch (err) {
      notify({
        type: 'error',
        title: 'Error',
        text: err.message || 'Error getting purchase order budget'
      })
      console.log(err)
    }
  } else {
    // @todo display error modal
  }
}

onMounted( async () => {
  if(form.getFieldValue('purchaseOrderId') === '') {
    form.setFieldValue('purchaseOrderId', await generatePurchaseOrderId())
    form.validateField('purchaseOrderId', 'blur')
  }
})
</script>
